import { override } from 'mobx';

import type { TConstraints } from '@feathr/rachis';

import { createDimensionValidator, CreativeClass, ValidDimensions } from './creatives_base';
import type { ICreativeAuditStatus, IDisplayCreative } from './creatives_display';
import { DisplayCreative } from './creatives_display';

// =============================================================================
// Ad Tag Creative Types
// =============================================================================

export interface IAdTagBaseCreative extends IDisplayCreative {
  readonly _cls: CreativeClass.DisplayBannersnack | CreativeClass.DisplayAdTag;
  readonly preview_url: string;
  adtag: string;
  preview_adtag: string;
  url: never;
}

export interface IAdTagCreative extends IAdTagBaseCreative {
  readonly _cls: CreativeClass.DisplayAdTag;
  audit_statuses: ICreativeAuditStatus[];
}

// =============================================================================
// Ad Tag Creative Type Guards
// =============================================================================

/**
 * Type guard to check if a creative is an AdTagCreative.
 *
 * @param creative - The creative to check
 * @returns True if the creative is an AdTagCreative
 */
export function isAdTagCreative(creative: DisplayCreative): creative is AdTagCreative {
  return creative.get('_cls') === CreativeClass.DisplayAdTag;
}

// =============================================================================
// Ad Tag Creative Class
// =============================================================================

/**
 * AdTagCreative handles third-party ad tag creatives.
 * These creatives use an ad tag instead of a direct URL.
 */
export class AdTagCreative extends DisplayCreative<IAdTagCreative> {
  public override readonly className = 'AdTagCreative';

  @override
  public override get creativeType(): string {
    return 'Ad Tag';
  }

  public override get constraints(): TConstraints<IAdTagCreative> {
    return {
      ...super.constraints,
      _cls: {
        inclusion: [CreativeClass.DisplayAdTag],
      },
      adtag: {
        presence: {
          allowEmpty: false,
        },
      },
      url: undefined,
      width: {
        ...super.constraints.width,
        equality: createDimensionValidator(ValidDimensions, 'width'),
      },
      height: {
        ...super.constraints.height,
        equality: createDimensionValidator(ValidDimensions, 'height'),
      },
    };
  }

  public override getDefaults(): Partial<IAdTagCreative> {
    return {
      ...super.getDefaults(),
      _cls: CreativeClass.DisplayAdTag,
    };
  }
}
